<script lang="ts" setup>
import { showConfirmDialog } from "vant";
import { computed, ref } from "vue";
import { ImagePromptItem, usePrompts } from "@/stores/prompts";
import PromptsCategory from "@/components/mobile/generation/PromptsCategory.vue";
import { useI18n } from "vue-i18n";
import i18n from "@/locales";
import { useRouter } from "vue-router";
import { onMounted } from "vue";
import {
  getPoseNsfwTag,
  getClothingNsfwTag,
  getOtherNsfwTag,
  getPosesTag,
} from "@/tools/nsfwPrompt";
import { nextTick } from "vue";
import { watch } from "vue";
import { promptListRemoveTag } from "@/modules/common";
// Props
const props = withDefaults(
  defineProps<{
    prompt: string;
    mode: string;
    showNegative?: boolean;
    isUnlimited?: boolean;
    showSideBar?: boolean;
    negativePrompt?: string;
    showFeatureParse?: boolean;
  }>(),
  {
    showNegative: true,
    isUnlimited: false,
    showSideBar: true,
    negativePrompt: "",
    showFeatureParse: false,
  }
);

// Emit
const emit = defineEmits(["update:prompt", "update:negativePrompt"]);

const { t } = useI18n();
const router = useRouter();

// State
const promptsStore = usePrompts();

const promptMap: Record<string, ImagePromptItem> = {};

const addPromptToMap = (list: ImagePromptItem[]) => {
  for (const item of list) {
    promptMap[item.text] = item;
    if (item.children) {
      addPromptToMap(item.children);
    }
  }
};

const prompts = computed(() => {
  let list: ImagePromptItem[] = [];
  const { mode } = props;
  if (mode === "character") {
    list.push(...promptsStore.characterCatePrompts);
  }
  if (mode === "single") {
    list.push(...promptsStore.singleCharacterPrompts);
    if (props.isUnlimited) {
      if (
        list[0].displayText !== "NSFW" &&
        list[1].children[0].displayText !== "NSFW"
      ) {
        list[1].children.unshift(getPoseNsfwTag());
        list[2].children.unshift(getClothingNsfwTag());
      }
      list.unshift(getOtherNsfwTag());
    } else {
      if (list[1].children[0].displayText === "NSFW") {
        list[1].children.shift();
        list[2].children.shift();
      }
    }
    list = JSON.parse(JSON.stringify(list));
  }

  if (mode === "multi_single") {
    list.push(...promptsStore.singlePrompts);
  }
  if (mode === "multi") {
    list.push(getPosesTag());
    list.push(...promptsStore.noCharacterPrompts);
  }
  if (mode === "nobody") {
    list.push(...promptsStore.noCharacterPrompts);
  }
  if (mode === "env") {
    list.push(
      ...promptsStore.cameraCatePrompts,
      ...promptsStore.envCatePrompts
    );
  }
  if (mode === "faceExpression") {
    list.push({
      id: 0,
      text: "",
      displayText: t("trait.expression"),
      path: "1",
      parentId: 1,
      children: promptsStore.faceExpressionPrompts,
    });
  }
  if (mode === "pose") {
    if (props.isUnlimited) list.push(getPoseNsfwTag());
    list.push(...promptsStore.posePrompts);
  }
  if (mode === "clothing") {
    if (props.isUnlimited) list.push(getClothingNsfwTag());
    list.push(...promptsStore.clothingPrompts);
  }
  if (mode === "style") {
    list.push(...promptsStore.stylePrompts);
  }
  if (mode === "face") {
    list.push(...promptsStore.facePrompts);
  }
  if (mode === "other") {
    list.push(getOtherNsfwTag());
  }
  addPromptToMap(list);
  if (searchText.value) {
    return searchTree(list, searchText.value);
  } else {
    return list;
  }
});
const currentTabIdx = ref(0);
const orderedSelectedTagMap = ref(new Map<number, ImagePromptItem>());
const isPositive = ref(true);
// Event Handler
const onPromptClick = (tag: ImagePromptItem) => {
  if (orderedSelectedTagMap.value.has(tag.id)) {
    orderedSelectedTagMap.value.delete(tag.id);
    let handlePrompt = isPositive.value ? props.prompt : props.negativePrompt;
    emit(
      isPositive.value ? "update:prompt" : "update:negativePrompt",
      promptListRemoveTag(handlePrompt, tag.text)
    );
  } else {
    orderedSelectedTagMap.value.set(tag.id, Object.assign({}, tag));
    let handlePrompt = isPositive.value ? props.prompt : props.negativePrompt;
    handlePrompt += handlePrompt ? `,${tag.text}` : tag.text;
    emit(
      isPositive.value ? "update:prompt" : "update:negativePrompt",
      handlePrompt
    );
  }
};

const clickEditPrompt = () => {
  if (props.prompt || props.negativePrompt) {
    showConfirmDialog({
      message: i18n.global.t("ConfirmModal.goingEditInfo"),
      confirmButtonText: i18n.global.t("Buttons.confirm"),
      cancelButtonText: i18n.global.t("Buttons.cancel"),
    })
      .then(() => {
        router.push(`/manager/prompt`);
      })
      .catch(() => {});
  } else router.push(`/manager/prompt`);
};

// 搜索
const searchText = ref("");

function searchTree(
  data: ImagePromptItem[],
  searchText: string
): ImagePromptItem[] {
  function searchNodes(nodes: ImagePromptItem[]): ImagePromptItem[] {
    const result: ImagePromptItem[] = [];

    for (const node of nodes) {
      const matchedChildren = searchNodes(node.children);

      if (
        node.text.toLowerCase().includes(searchText.toLowerCase()) ||
        node.displayText.toLowerCase().includes(searchText.toLowerCase()) ||
        matchedChildren.length > 0
      ) {
        result.push({
          ...node,
          children: matchedChildren,
        });
      }
    }

    return result;
  }

  return searchNodes(data);
}

const initOrderSelectedTag = (_isPositive: boolean) => {
  isPositive.value = _isPositive;
  orderedSelectedTagMap.value.clear();
  const text = _isPositive ? props.prompt : props.negativePrompt;
  text.split(",").forEach((item) => {
    item = item.trim();
    const [label] = item.split(":");
    const tag = promptMap[label] || {};
    if (tag.id) orderedSelectedTagMap.value.set(tag.id, Object.assign({}, tag));
  });
};
watch(
  () => props.prompt,
  (val) => {
    if (isPositive.value) {
      initOrderSelectedTag(true);
    }
  }
);

onMounted(async () => {
  await promptsStore.getOrFetchImgPrompts();
  nextTick(() => initOrderSelectedTag(true));
});
</script>

<template>
  <div class="h-full overflow-hidden grid grid-rows-[auto_auto_1fr] border-t">
    <div class="mx-2">
      <InputTag
        :show-feature-parse="showFeatureParse"
        :show-negative="showNegative"
        :isPositive="isPositive"
        :prompt="prompt"
        :negativePrompt="negativePrompt"
        @update:prompt="emit('update:prompt', $event)"
        @update:negativePrompt="emit('update:negativePrompt', $event)"
        @update:is-positive="initOrderSelectedTag"
      />
    </div>

    <div class="mx-2.5 my-3 flex items-center gap-3">
      <p>
        {{ t(`Buttons.lexicon`) }}
      </p>

      <button class="btn btn-xs rounded mr-1" @click="clickEditPrompt">
        <i class="iconify mingcute--edit-3-line" />
        {{ $t("Buttons.editLexicon") }}
      </button>

      <VanSearch
        v-model="searchText"
        :placeholder="t(`Message.search`)"
        clearable
        class="flex-1 rounded-2xl py-0 px-0 mx-3"
      />
    </div>
    <div class="w-full h-full grid grid-cols-[auto_1fr] overflow-hidden">
      <div
        class="border-r border-r-base-content/10 flex flex-col gap-2 items-center overflow-y-auto"
        v-if="showSideBar"
      >
        <VanSidebar v-model="currentTabIdx" class="w-[6rem] pb-5">
          <VanSidebarItem
            v-for="category in prompts"
            :key="category.id"
            :title="category.displayText"
          />
        </VanSidebar>
      </div>
      <div class="flex-grow overflow-y-auto p-2">
        <div :class="['flex', !showSideBar && 'flex-col']">
          <div
            v-for="(category, idx) in prompts"
            :key="category.id"
            v-show="idx === currentTabIdx || !showSideBar"
            class="flex flex-col gap-4"
          >
            <div class="flex flex-wrap">
              <prompts-category
                :level="1"
                :list="showSideBar ? category.children : [category]"
                :selected-tag-map="orderedSelectedTagMap"
                @prompt-click="onPromptClick"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
:deep(.van-search__content) {
  background-color: var(--color-primary);
}
</style>
